import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import competencyApi from "../api/competencyApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import { buildReadableCode } from "../utils/performanceCode";

const CompetencyCategoriesPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadCompetencies = can(PERFORMANCE_PERMISSIONS.COMPETENCIES_READ);
  const canCreateCompetencies = can(
    PERFORMANCE_PERMISSIONS.COMPETENCIES_CREATE,
  );

  if (!canReadCompetencies) {
    return (
      <ModulePageShell
        title="Competency Categories"
        description="Define category taxonomy for competency assessments."
      >
        <EmptyState message="You do not have permission to view competency categories." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.competencyCategories],
    queryFn: () => competencyApi.listCategories({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: competencyApi.createCategory,
    onSuccess: () => {
      toast({ title: "Category created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.competencyCategories],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create category",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: "",
      description: "",
      status: "ACTIVE",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Competency Categories"
      description="Define category taxonomy for competency assessments."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateCompetencies ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                code: buildReadableCode("CC", values.name),
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Category</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input {...register("name")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select {...register("status")}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input {...register("description")} />
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Category
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to competency categories, but cannot create categories." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No competency categories found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Category</Th>
                <Th>Description</Th>
                <Th>Status</Th>
                <Th>Created Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.name}</Td>
                  <Td>{item.description || "-"}</Td>
                  <Td>{item.status || "-"}</Td>
                  <Td>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString()
                      : "-"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default CompetencyCategoriesPage;
